// Styles
import './OmniSearch.scss';

// Local Util
// GraphQL Types
import {
  OmniSearchApplicationByIdQuery,
  OmniSearchApplicationByIdQueryVariables,
} from '~/graphqlTypes/OmniSearchApplicationByIdQuery';
import {
  OmniSearchApplicationByTokenQuery,
  OmniSearchApplicationByTokenQueryVariables,
} from '~/graphqlTypes/OmniSearchApplicationByTokenQuery';
import {
  OmniSearchAudioFileByIdQuery,
  OmniSearchAudioFileByIdQueryVariables,
  OmniSearchAudioFileByIdQuery_audioFile,
} from '~/graphqlTypes/OmniSearchAudioFileByIdQuery';
import {
  OmniSearchPlacementByIdQuery,
  OmniSearchPlacementByIdQueryVariables,
  OmniSearchPlacementByIdQuery_placement,
} from '~/graphqlTypes/OmniSearchPlacementByIdQuery';
import {
  OmniSearchStationByIdQuery,
  OmniSearchStationByIdQueryVariables,
  OmniSearchStationByIdQuery_station,
} from '~/graphqlTypes/OmniSearchStationByIdQuery';
import {
  OmniSearchSubstringQuery,
  OmniSearchSubstringQueryVariables,
  OmniSearchSubstringQuery_users_results_user,
} from '~/graphqlTypes/OmniSearchSubstringQuery';
import { QueryResult, useLazyQuery } from '@apollo/client';
import React, { ReactElement } from 'react';

import { ActionMeta } from 'react-select';
import AsyncSelect from 'react-select/async';
import { Badge } from 'react-bootstrap';
import { linkToStationDetails } from '~/util/linkBuilders';
// Types
// Queries
import omniSearchApplicationByIdQuery from './OmniSearchApplicationByIdQuery.gql';
import omniSearchApplicationByTokenQuery from './OmniSearchApplicationByTokenQuery.gql';
import omniSearchAudioFileByIdQuery from './OmniSearchAudioFileByIdQuery.gql';
import omniSearchPlacementByIdQuery from './OmniSearchPlacementByIdQuery.gql';
import omniSearchStationQueryByd from './OmniSearchStationByIdQuery.gql';
import omniSearchSubstringQuery from './OmniSearchSubstringQuery.gql';
import { useDebouncedCallback } from 'use-debounce';
import { useRecentSearches } from '~/hooks/useRecentSearches';

// Global Components

// Global Util

// Subcomponents

interface CreateOption {
  label: string;
  value: string;
  __isNew__: true;
}

interface SearchOption {
  label: string | ReactElement;
  value: string;
  type: 'search';
  url: string;
}

interface ApplicationOption {
  label: string | ReactElement;
  value: string;
  type: 'application';
  application: {
    id: number;
    name: string;
    instances: {
      id: number;
      name: string | null;
      development: { id: number };
    } | null;
  }
  url: string;
}

interface ApplicationInstanceSetOption {
  label: string | ReactElement;
  value: string;
  type: 'application-instance-set';
  application: {
    id: number;
    name: string;
    instances: {
      id: number;
      name: string | null;
      development: { id: number };
    } | null;
  }
  url: string;
}

interface AudioFileOption {
  label: string | ReactElement;
  value: string;
  type: 'audioFile';
  audioFile: OmniSearchAudioFileByIdQuery_audioFile;
  url: string;
}

interface PlacementOption {
  label: string | ReactElement;
  value: string;
  type: 'placement';
  placement: OmniSearchPlacementByIdQuery_placement;
  url: string;
}

interface StationOption {
  label: string | ReactElement;
  value: string;
  type: 'station';
  station: OmniSearchStationByIdQuery_station;
  url: string;
}

interface UserOption {
  label: string | ReactElement;
  value: string;
  type: 'user';
  user: OmniSearchSubstringQuery_users_results_user;
  url: string;
}

export type RecentSearchOption =
  | ApplicationOption
  | ApplicationInstanceSetOption
  | AudioFileOption
  | PlacementOption
  | StationOption
  | SearchOption
  | CreateOption
  | UserOption;

interface GroupOption {
  label: string;
  type: 'group';
  options: Array<RecentSearchOption>;
}

export interface OmniSearchProps {
  // Uncomment this if your component requires children
  // children: ReactNode;
  className?: string;
}

function formatOption( option: RecentSearchOption ): ReactElement {
  if ( '__isNew__' in option || option.type === 'search' ) {
    return <span>{ option.label }</span>;
  } else if ( option.type === 'application-instance-set' ) {
    return (
      <span>
        <Badge variant="light">application</Badge> { option.label } <span className="details">{ option.value }</span>
      </span>
    );
  } else {
    return (
      <span>
        <Badge variant="light">{ option.type }</Badge> { option.label } <span className="details">{ option.value }</span>
      </span>
    );
  }
}

function formatIdSearchResults(
  appResults: QueryResult<OmniSearchApplicationByIdQuery, OmniSearchApplicationByIdQueryVariables>,
  audioFileResults: QueryResult<OmniSearchAudioFileByIdQuery, OmniSearchAudioFileByIdQueryVariables>,
  placementResults: QueryResult<OmniSearchPlacementByIdQuery, OmniSearchPlacementByIdQueryVariables>,
  stationResults: QueryResult<OmniSearchStationByIdQuery, OmniSearchStationByIdQueryVariables>,
): Array<GroupOption> {
  const options: Array<RecentSearchOption> = [];

  if ( !appResults.error && appResults.data ) {
    const app = appResults.data.application;

    if ( app.instances ) {
      options.push( {
        label: app.instances.name || app.name,
        value: app.instances.development.id.toString(),
        type: 'application-instance-set',
        application: app,
        url: `/appstation/${app.id}`,
      } );
    } else {
      options.push( {
        label: app.name,
        value: app.id.toString(),
        type: 'application',
        application: app,
        url: `/application/${app.id}`,
      } );
    }
  }

  if ( !placementResults.error && placementResults.data ) {
    const placement = placementResults.data.placement;

    options.push( {
      label: placement.name,
      value: placement.id.toString(),
      type: 'placement',
      placement: placement,
      url: `/placements/${placement.id}`,
    } );
  }

  if ( !audioFileResults.error && audioFileResults.data ) {
    const audioFile = audioFileResults.data.audioFile;

    options.push( {
      label: audioFile.track.title + ' by ' + audioFile.artist.name,
      value: audioFile.id.toString(),
      type: 'audioFile',
      audioFile: audioFile,
      url: `/catalog/search?q=id:${audioFile.id}`,
    } );
  }

  if ( !stationResults.error && stationResults.data ) {
    const station = stationResults.data.station;

    options.push( {
      label: station.name,
      value: station.id.toString(),
      type: 'station',
      station: station,
      url: linkToStationDetails( station.id ),
    } );
  }

  if ( options.length === 0 ) {
    return [];
  } else {
    return [
      {
        label: 'ID matches',
        type: 'group',
        options: options,
      },
    ];
  }
}

function appToOption( app: { id: number; name: string; instances: { id: number; name: string | null; development: { id: number }; production: { id: number } } | null } ): RecentSearchOption {
  if ( app.instances ) {
    return {
      label: app.instances.name || app.name,
      value: app.instances.development.id.toString(),
      type: 'application-instance-set',
      application: app,
      url: `/appstation/${app.id}`,
    };
  } else {
    return {
      label: app.name,
      value: app.id.toString(),
      type: 'application',
      application: app,
      url: `/application/${app.id}`,
    };
  }
}

function formatTokenSearchResults(
  tokenResults: QueryResult<OmniSearchApplicationByTokenQuery, OmniSearchApplicationByTokenQueryVariables>,
): Array<GroupOption> {
  if ( tokenResults.error || !tokenResults.data?.applicationByToken ) {
    return [];
  }

  return [
    {
      label: 'Token matches',
      type: 'group',
      options: [appToOption( tokenResults.data.applicationByToken )],
    },
  ];
}

function formatSubstringSearchResults(
  query: string,
  results: QueryResult<OmniSearchSubstringQuery, OmniSearchSubstringQueryVariables>,
): Array<GroupOption> {
  const appOptions: Array<RecentSearchOption> = [];

  if ( results.data?.applications.total ) {
    const existing = new Set();

    for ( let result of results.data.applications.results ) {
      const app = result.application;

      if ( !existing.has( app.id ) ) {
        if ( app.instances ) {
          existing.add( app.instances.development.id );
          existing.add( app.instances.production.id );
        } else {
          existing.add( app.id );
        }

        appOptions.push( appToOption( app ) );
      }
    }

    if ( results.data.applications.hasMore ) {
      appOptions.push( {
        label: `all apps matching "${query}"...`,
        value: query,
        type: 'search',
        url: '/search?q=' + encodeURIComponent( query ),
      } );
    }
  }

  const userOptions: Array<RecentSearchOption> = [];

  if ( results.data?.users.total ) {
    for ( let result of results.data.users.results ) {
      userOptions.push( {
        label: result.user.emailAddress,
        value: result.user.id.toString(),
        type: 'user',
        user: result.user,
        url: `/user/${result.user.id}`,
      } );
    }

    if ( results.data.users.hasMore ) {
      userOptions.push( {
        label: `all users matching "${query}"...`,
        value: query,
        type: 'search',
        url: '/search?q=' + encodeURIComponent( query ),
      } );
    }
  }

  if ( appOptions.length === 0 && userOptions.length === 0 ) {
    return [];
  } else {
    if ( query.includes( '@' ) ) {
      return [
        {
          label: 'Substring matches',
          type: 'group',
          options: [...userOptions, ...appOptions],
        },
      ];
    } else {
      return [
        {
          label: 'Substring matches',
          type: 'group',
          options: [...appOptions, ...userOptions],
        },
      ];
    }
  }
}

export const OmniSearch = ( props: OmniSearchProps ) => {
  const { className } = props;

  const { recentSearches, addRecentSearch } = useRecentSearches();

  const recentSearchesGroup: Array<GroupOption> =
    recentSearches.length > 0 ? [{ label: 'Recent searches...', type: 'group', options: recentSearches }] : [];

  const [searchApplicationById, { loading: loadingApplicationById }] = useLazyQuery<
  OmniSearchApplicationByIdQuery,
  OmniSearchApplicationByIdQueryVariables
  >( omniSearchApplicationByIdQuery );
  const [searchAudioFileById, { loading: loadingAudioFileById }] = useLazyQuery<
  OmniSearchAudioFileByIdQuery,
  OmniSearchAudioFileByIdQueryVariables
  >( omniSearchAudioFileByIdQuery );
  const [searchPlacementById, { loading: loadingPlacementById }] = useLazyQuery<
  OmniSearchPlacementByIdQuery,
  OmniSearchPlacementByIdQueryVariables
  >( omniSearchPlacementByIdQuery );
  const [searchStationById, { loading: loadingStationById }] = useLazyQuery<
  OmniSearchStationByIdQuery,
  OmniSearchStationByIdQueryVariables
  >( omniSearchStationQueryByd );
  const [searchApplicationByToken, { loading: loadingApplicationByToken }] = useLazyQuery<
  OmniSearchApplicationByTokenQuery,
  OmniSearchApplicationByTokenQueryVariables
  >( omniSearchApplicationByTokenQuery );
  const [substring, { loading: loadingSubstring }] = useLazyQuery<
  OmniSearchSubstringQuery,
  OmniSearchSubstringQueryVariables
  >( omniSearchSubstringQuery );

  const loadOptions = ( query: string, callback: ( options: Array<GroupOption> ) => void ): void => {
    const numericQuery = parseInt( query );

    if ( isNaN( numericQuery ) ) {
      const queries: [
        Promise<QueryResult<OmniSearchSubstringQuery, OmniSearchSubstringQueryVariables>>,
        Promise<QueryResult<OmniSearchApplicationByTokenQuery, OmniSearchApplicationByTokenQueryVariables>> | null,
      ] = [
        substring( { variables: { searchTerm: query } } ),
        query.length >= 4 ? searchApplicationByToken( { variables: { token: query } } ) : null,
      ];

      Promise.all( queries ).then( async ( [substringResults, tokenResults] ) => {
        const substringOptions = formatSubstringSearchResults( query, substringResults! );
        const tokenOptions = tokenResults ? formatTokenSearchResults( tokenResults ) : [];

        callback( [...tokenOptions, ...substringOptions, ...recentSearchesGroup] );
      } );
    } else {
      Promise.all( [
        searchApplicationById( { variables: { applicationId: query } } ),
        searchAudioFileById( { variables: { audioFileId: query } } ),
        searchPlacementById( { variables: { placementId: +query } } ),
        searchStationById( { variables: { stationId: query } } ),
        query.length >= 4 ? searchApplicationByToken( { variables: { token: query } } ) : null,
      ] ).then( async ( [appResults, audioFileResults, placementResults, stationResults, tokenResults] ) => {
        const newOptions = formatIdSearchResults( appResults, audioFileResults, placementResults, stationResults );
        const tokenOptions = tokenResults ? formatTokenSearchResults( tokenResults ) : [];

        callback( [...tokenOptions, ...newOptions, ...recentSearchesGroup] );
      } );
    }
  };

  const debouncedLoadOptions = useDebouncedCallback( loadOptions, 500 );

  const handleSelect = (
    option: RecentSearchOption | GroupOption | null,
    { action }: ActionMeta<RecentSearchOption>,
  ) => {
    if ( !option || '__isNew__' in option || option.type === 'group' || action !== 'select-option' ) {
      console.log( 'ignoring', option, action );
      return;
    }

    addRecentSearch( option );

    if ( option.url ) {
      window.location.assign( option.url );
    }
  };

  const loading =
    loadingApplicationById || loadingApplicationByToken || loadingAudioFileById || loadingPlacementById || loadingStationById || loadingSubstring;

  return (
    <AsyncSelect
      className={ className }
      classNamePrefix="ops-omni-search"
      blurInputOnSelect={ true }
      closeMenuOnSelect={ true }
      defaultOptions={ recentSearchesGroup }
      isLoading={ loading }
      loadOptions={ debouncedLoadOptions }
      isMulti={ false }
      onChange={ handleSelect }
      value={ null }
      formatOptionLabel={ ( option ) => formatOption( option ) }
      placeholder="Enter search term or object ID..."
      components={ { IndicatorSeparator: () => <></>, DropdownIndicator: () => <></> } }
    />
  );
};

OmniSearch.displayName = 'OmniSearch';

/*
  Specify permissions required to access the component.
  Must be paired with <RestrictedAccess /> to work properly.
*/
OmniSearch.requiredPermissions = [] as string[];
